/**
 * ATG customization (2026-08-28): product page shipping notification.
 * Renders "Usually ships today/tomorrow/<weekday>" with a live countdown to the
 * next noon Eastern cutoff, or "Usually ships within N business days" when the
 * product has a shipping delay metafield. All times are computed in
 * America/New_York (DST-safe), skipping weekends and the configured holidays.
 * State is fully recomputed on every tick, so crossing midnight or the noon
 * cutoff while the page is open is handled naturally.
 *
 * Config (from the snippet):
 *   data-delay    - integer business days before the product ships (0 = normal)
 *   data-holidays - comma/newline separated holiday names, e.g.
 *                   "New Year's Day, Memorial Day, Independence Day, Labor Day,
 *                   Thanksgiving, Christmas" (dates are computed per year)
 *
 * QA: append ?atg-time=<ISO-8601 UTC date> to any product URL to simulate a
 * different moment, e.g. ?atg-time=2026-09-04T18:30:00Z (Friday 2:30pm ET).
 */
class AtgShippingNotice extends HTMLElement {
  static TIME_ZONE = 'America/New_York';
  static CUTOFF_HOUR = 12;

  connectedCallback() {
    if (this.dataset.instant === 'true') {
      // Pre-rendered by Liquid from the theme setting; nothing to compute.
      this.removeAttribute('hidden');
      return;
    }
    this.update();
    this.timer = setInterval(() => this.update(), 30 * 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  update() {
    const textEl = this.querySelector('[data-atg-shipping-text]');
    if (!textEl) return;

    const now = this.simulatedNow() || new Date();
    const delay = parseInt(this.dataset.delay, 10) || 0;
    const holidays = this.holidayDates(this.dataset.holidays || '', now);

    let message;
    if (delay > 0) {
      message = this.delayMessage(now, holidays, delay);
    } else {
      message = this.nextShipmentMessage(now, holidays);
    }

    textEl.textContent = message;
    this.removeAttribute('hidden');
  }

  /**
   * Resolves holiday names (per the theme setting) to YYYY-MM-DD date keys for
   * the years around "now". Unrecognized names are ignored. Fixed holidays
   * falling on a Saturday or Sunday are observed the following Monday.
   */
  holidayDates(names, now) {
    const year = this.etParts(now).y;
    const keys = new Set();
    for (const raw of names.split(/[,\n]/)) {
      const name = raw.trim().toLowerCase().replace(/[^a-z0-9 ]/g, '');
      if (!name) continue;
      const resolver = this.constructor.HOLIDAYS.find((holiday) =>
        holiday.names.includes(name)
      );
      if (!resolver) continue;
      for (const y of [year - 1, year, year + 1]) {
        const date = resolver.date(y);
        keys.add(this.dateKey(date.y, date.m, date.d));
        const weekday = new Date(Date.UTC(date.y, date.m - 1, date.d)).getUTCDay();
        if (weekday === 0 || weekday === 6) {
          const observed = this.addDays(date.y, date.m, date.d, (8 - weekday) % 7); // next Monday
          keys.add(this.dateKey(observed.y, observed.m, observed.d));
        }
      }
    }
    return keys;
  }

  /**
   * Delayed-shipping message, mirroring the normal phrasing: the ship day is
   * the delay-th shipping day (day 1 = today when ordering before the cutoff,
   * otherwise the next shipping day).
   *   - Delays of 5 business days or fewer: "Usually ships on <Weekday>." (or
   *     today/tomorrow when it lands there) with a live countdown to the next
   *     noon cutoff.
   *   - Longer delays: "Usually ships by <Month D>." — a full date instead of a
   *     weekday, since the day name stops being meaningful. Countdown still
   *     reflects the time remaining to keep the earliest possible ship date.
   */
  delayMessage(now, holidays, delay) {
    const parts = this.etParts(now);
    const cutoffToday = this.etMs(parts.y, parts.m, parts.d, this.constructor.CUTOFF_HOUR, 0);
    let day1;
    if (this.isShippingDay(parts.y, parts.m, parts.d, holidays) && now.getTime() < cutoffToday) {
      day1 = { y: parts.y, m: parts.m, d: parts.d };
    } else {
      day1 = this.nextShippingDay(parts.y, parts.m, parts.d, holidays);
    }

    let ship = day1;
    for (let i = 1; i < delay; i++) {
      ship = this.nextShippingDay(ship.y, ship.m, ship.d, holidays);
    }

    const cutoffDay1 = this.etMs(day1.y, day1.m, day1.d, this.constructor.CUTOFF_HOUR, 0);
    const countdown = 'Order within ' + this.humanize(cutoffDay1 - now.getTime()) + '.';

    if (delay >= 6) {
      return 'Usually ships by ' + this.monthDay(ship.y, ship.m, ship.d) + '. ' + countdown;
    }

    const tomorrow = this.addDays(parts.y, parts.m, parts.d, 1);
    const isToday =
      ship.d === parts.d && ship.m === parts.m && ship.y === parts.y;
    const isTomorrow =
      ship.d === tomorrow.d && ship.m === tomorrow.m && ship.y === tomorrow.y;
    let label;
    if (isToday) {
      label = 'Usually ships today. ';
    } else if (isTomorrow) {
      label = 'Usually ships tomorrow. ';
    } else {
      label = 'Usually ships on ' + this.weekdayName(ship.y, ship.m, ship.d) + '. ';
    }
    return label + countdown;
  }

  monthDay(y, m, d) {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return months[m - 1] + ' ' + d;
  }

  nextShipmentMessage(now, holidays) {
    const parts = this.etParts(now);
    const cutoffToday = this.etMs(parts.y, parts.m, parts.d, this.constructor.CUTOFF_HOUR, 0);

    if (this.isShippingDay(parts.y, parts.m, parts.d, holidays) && now.getTime() < cutoffToday) {
      return (
        'Usually ships today. Order within ' +
        this.humanize(cutoffToday - now.getTime()) +
        '.'
      );
    }

    const next = this.nextShippingDay(parts.y, parts.m, parts.d, holidays);
    const tomorrow = this.addDays(parts.y, parts.m, parts.d, 1);
    const isTomorrow = next.d === tomorrow.d && next.m === tomorrow.m && next.y === tomorrow.y;
    const dayName = isTomorrow ? 'tomorrow' : this.weekdayName(next.y, next.m, next.d);
    const nextCutoff = this.etMs(next.y, next.m, next.d, this.constructor.CUTOFF_HOUR, 0);

    return (
      'Usually ships ' +
      dayName +
      '. Order within ' +
      this.humanize(nextCutoff - now.getTime()) +
      '.'
    );
  }

  nextShippingDay(y, m, d, holidays) {
    let next = this.addDays(y, m, d, 1);
    let guard = 0;
    while (!this.isShippingDay(next.y, next.m, next.d, holidays) && guard < 30) {
      next = this.addDays(next.y, next.m, next.d, 1);
      guard++;
    }
    return next;
  }

  isShippingDay(y, m, d, holidays) {
    const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    return weekday >= 1 && weekday <= 5 && !holidays.has(this.dateKey(y, m, d));
  }

  /**
   * Humans and countdowns only: converts an America/New_York wall-clock time to
   * a real UTC timestamp, refining once for DST boundary correctness.
   */
  etMs(y, m, d, hh, mm) {
    const guess = Date.UTC(y, m - 1, d, hh, mm, 0);
    const firstPass = guess - this.zoneOffset(guess);
    return guess - this.zoneOffset(firstPass);
  }

  zoneOffset(utcMs) {
    const parts = this.etParts(new Date(utcMs));
    const asUtc = Date.UTC(parts.y, parts.m - 1, parts.d, parts.hh, parts.mm, parts.ss);
    return asUtc - utcMs;
  }

  etParts(date) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: this.constructor.TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    const values = {};
    for (const part of formatter.formatToParts(date)) {
      if (part.type !== 'literal') values[part.type] = parseInt(part.value, 10);
    }
    return { y: values.year, m: values.month, d: values.day, hh: values.hour % 24, mm: values.minute, ss: values.second };
  }

  addDays(y, m, d, days) {
    const shifted = new Date(Date.UTC(y, m - 1, d) + days * 86400000);
    return { y: shifted.getUTCFullYear(), m: shifted.getUTCMonth() + 1, d: shifted.getUTCDate() };
  }

  weekdayName(y, m, d) {
    return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][
      new Date(Date.UTC(y, m - 1, d)).getUTCDay()
    ];
  }

  dateKey(y, m, d) {
    const pad = (value) => String(value).padStart(2, '0');
    return y + '-' + pad(m) + '-' + pad(d);
  }

  humanize(ms) {
    const totalMinutes = Math.max(0, Math.round(ms / 60000));
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    const units = [];
    if (days > 0) units.push(this.unit(days, 'day'));
    if (hours > 0 && units.length < 2) units.push(this.unit(hours, 'hour'));
    if (minutes > 0 && units.length < 2) units.push(this.unit(minutes, 'minute'));
    if (units.length === 0) return 'a moment';
    return units.join(' ');
  }

  unit(value, name) {
    return value + ' ' + name + (value === 1 ? '' : 's');
  }

  simulatedNow() {
    if (!window.location || !window.location.search) return null;
    const value = new URLSearchParams(window.location.search).get('atg-time');
    if (!value) return null;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
}

/**
 * Floating-holiday math: first/last/nth weekday of a month in UTC calendar days
 * (the timezone only matters for the "now" comparison, not the date itself).
 */
AtgShippingNotice.HOLIDAYS = [
  {
    names: ['new years day', 'new year'],
    date: (y) => ({ y, m: 1, d: 1 }),
  },
  {
    names: ['memorial day'],
    date: (y) => AtgShippingNotice.lastWeekday(y, 5, 1),
  },
  {
    names: ['independence day', 'july 4', '4th of july'],
    date: (y) => ({ y, m: 7, d: 4 }),
  },
  {
    names: ['labor day'],
    date: (y) => AtgShippingNotice.firstWeekday(y, 9, 1),
  },
  {
    names: ['thanksgiving'],
    date: (y) => AtgShippingNotice.nthWeekday(y, 11, 4, 4),
  },
  {
    names: ['christmas', 'xmas'],
    date: (y) => ({ y, m: 12, d: 25 }),
  },
];

AtgShippingNotice.weekdayOfMonth = function (y, m, d) {
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
};

AtgShippingNotice.firstWeekday = function (y, m, weekday) {
  let d = 1;
  while (AtgShippingNotice.weekdayOfMonth(y, m, d) !== weekday) d++;
  return { y, m, d };
};

AtgShippingNotice.lastWeekday = function (y, m, weekday) {
  let d = new Date(Date.UTC(y, m, 0)).getUTCDate();
  while (AtgShippingNotice.weekdayOfMonth(y, m, d) !== weekday) d--;
  return { y, m, d };
};

AtgShippingNotice.nthWeekday = function (y, m, n, weekday) {
  const first = AtgShippingNotice.firstWeekday(y, m, weekday);
  return { y, m, d: first.d + (n - 1) * 7 };
};

customElements.define('atg-shipping-notice', AtgShippingNotice);
